import { SupportMessageProps } from '@mrshmllw/smores-react'

import { UsageType, VehicleType } from 'types'

export type RadioOptionUsageTypeContentItem = {
  label: string
  bodyCopy: string
}

const carShownUsageTypes = [UsageType.SDP, UsageType.SDPC, UsageType.CLASS_1] as const

const vanShownUsageTypes = [
  UsageType.SDP,
  UsageType.SDPC,
  UsageType.CARRIAGE_OF_OWN_GOODS,
  UsageType.HAULAGE,
] as const

export const getUsageOptions = ({ isVan }: { isVan: boolean }) => {
  if (isVan) {
    return vanShownUsageTypes
  }

  return carShownUsageTypes
}

export const generateUsageTypeRadioOptions = ({
  vehicleType,
  isHealthCareOccupation,
}: {
  vehicleType: VehicleType
  isHealthCareOccupation?: boolean
}) => {
  const usageOptions = getUsageOptions({
    isVan: vehicleType === VehicleType.VAN,
  })

  const radioOptionsContent = createRadioOptionsContentByUsageType({
    isHealthCareOccupation,
  })

  return usageOptions.map((option) => {
    const content = radioOptionsContent[option]

    return {
      ...content,
      value: option,
    }
  })
}

export const createRadioOptionsContentByUsageType = ({
  isHealthCareOccupation,
}: {
  isHealthCareOccupation?: boolean
}): Record<UsageType, RadioOptionUsageTypeContentItem> => ({
  [UsageType.SDP]: {
    label: 'Social journeys only (SDP)',
    bodyCopy:
      'For example, shopping, travel and driving to see friends. Does not include travelling to and from work.',
  },
  [UsageType.SDPC]: {
    label: 'Driving to and from work (SDPC)',
    bodyCopy: isHealthCareOccupation
      ? 'Social journeys, plus driving to and from one work location. For example, a hospital or care home.'
      : 'Social journeys, plus driving to and from one work location. For example, an office, shop or factory.',
  },
  [UsageType.CLASS_1]: {
    label: 'Driving during working hours (BC1)',
    bodyCopy: isHealthCareOccupation
      ? 'Business Class 1 also lets you travel between multiple work locations. For example, driving between client homes carrying basic PPE.'
      : "Business Class 1 also lets you travel between multiple work locations. For example, if you're a real estate agent driving between houses.",
  },
  [UsageType.CLASS_3]: {
    label: 'Commercial use',
    bodyCopy:
      'For people like courier, taxi and delivery drivers. Covers carriage of work-related tools and goods.',
  },
  [UsageType.CARRIAGE_OF_OWN_GOODS]: {
    label: 'I use it for work (Carriage of own goods)',
    bodyCopy: 'All the above, plus cover to carry work-related goods and tools for trade/business.',
  },
  [UsageType.HAULAGE]: {
    label: "Haulage (Carriage of other people's goods)",
    bodyCopy:
      'Cover to deliver bulk loads over longer distances. We do not offer this kind of cover.',
  },
})

export const createSupportMessageContent = (
  isHealthCareOccupation: boolean,
): Partial<Record<UsageType, SupportMessageProps>> => ({
  [UsageType.SDP]: {
    description: 'Suitable for stay-at-home parents, people who work from home etc.',
    type: 'success',
  },
  [UsageType.SDPC]: {
    description: isHealthCareOccupation
      ? 'Suitable for staff and healthcare professionals working at the same hospital, surgery or care home every day.'
      : 'Suitable for office, shop or factory workers who travel to the same building every day.',
    type: 'success',
  },
  [UsageType.CLASS_1]: {
    title: isHealthCareOccupation
      ? 'Driving with patients or equipment?'
      : 'Carrying customers or equipment?',
    description: isHealthCareOccupation
      ? 'If you carry patients, people or medical equipment in your car as part of your job, you need commercial cover. We don’t offer this type of insurance, and we cannot insure you at this time.'
      : 'If you use your car to make deliveries, for hire and reward, or any business purpose that requires you to carry goods, tools, or people, you need commercial cover. We don’t offer this type of insurance, and we cannot insure you at this time.',
    type: 'alert',
  },
})
